import { Injectable } from '@angular/core';

import { map, Observable } from 'rxjs';

import { WeightClass } from '@reg/shared/model';
import { HttpServiceHelper } from '@reg/shared/service/http-service-helper';
import { IJsonApiResponse } from '../json-api/json-api.interface';

/**
 * Service to get and save a wrestling weight class.
 */
@Injectable()
export class WeightClassService {
    constructor(private http: HttpServiceHelper) {
    }

    /**
     * Get all the weight classes for a given event and division
     */
    public getIndex(eventId: string, divisionId: string): Observable<WeightClass[]> {
        const apiRequestUrl: string = this.getApiUrl(eventId, divisionId);

        return this.http.get(apiRequestUrl)
            .pipe(map((response: IJsonApiResponse) => {
                const weightClasses: WeightClass[] = [];
                const bodyData: any = response.data || [];
                for (const weightClass of bodyData) {
                    weightClasses.push((new WeightClass()).deserialize(weightClass));
                }
                return weightClasses;
            }));
    }

    /**
     * Add or update a weight class
     */
    public save(eventId: string, divisionId: string, weightClass: WeightClass): Observable<WeightClass> {
        let response: Observable<IJsonApiResponse>;

        if (!weightClass.isNew) {
            // Update existing Division
            const apiRequestUrl: string = this.getApiUrl(eventId, divisionId) + weightClass.id;
            response = this.http.patch(apiRequestUrl, weightClass.serialize());
        } else {
            // Add a new Division
            const apiRequestUrl: string = this.getApiUrl(eventId, divisionId);
            response = this.http.post(apiRequestUrl, weightClass.serialize());
        }

        return response.pipe(map((response: IJsonApiResponse) => (new WeightClass()).deserialize(response)));
    }

    /**
     * Delete a weight class. This service will fail if a wrestler is linked the weight class to
     * be removed.
     */
    public delete(eventId: string, divisionId: string, weightClassId: string): Observable<boolean> {
        const apiRequestUrl: string = this.getApiUrl(eventId, divisionId) + weightClassId;
        return this.http.delete(apiRequestUrl).pipe(map((response: IJsonApiResponse) => true));
    }

    /**
     * Build the api's url based on the given parameters
     */
    private getApiUrl(eventId: string, divisionId: string): string {
        return `${this.http.configService.getApiBaseUrl()}/events/${eventId}/divisions/${divisionId}/weight-classes/`;
    }
}
